import { ROUTE } from '../../routes';

/**
 * The "New" markers on the blog list (`docs/auto-generation.md` in spark-blog,
 * "Owner Studio behaviour").
 *
 * "See articles" on the Home Spotlight card writes the ids of the Autopilot
 * articles the owner has not looked at yet under a per-brand key; the blog list
 * badges those rows "New"; opening an article's edit or preview page removes
 * its id; and leaving the blog area (`isBlogPath`) clears every brand's list.
 *
 * sessionStorage, deliberately: the markers are a reading aid for ONE visit,
 * not state anyone else needs, and a tab closed is a visit over. Every access
 * is guarded because storage can be missing or refuse (private mode, quota, a
 * sandboxed frame); with no storage there are simply no badges this session.
 */

/** Prefix of the per-brand key; the brand id follows it. */
export const NEW_ARTICLES_KEY_PREFIX = 'ows.blog.newArticles.';

export const newArticlesKey = (brandId: string): string => `${NEW_ARTICLES_KEY_PREFIX}${brandId}`;

/** The ids marked new for a brand, or `[]` (nothing stored, storage unavailable, or an unreadable value). */
export const getNewArticles = (brandId: string): string[] => {
  try {
    const raw = sessionStorage.getItem(newArticlesKey(brandId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string' && id !== '');
  } catch {
    return [];
  }
};

/** Replace a brand's list. Duplicates and empty ids are dropped; an empty list removes the key. */
export const setNewArticles = (brandId: string, ids: readonly string[]): void => {
  try {
    const unique = Array.from(new Set(ids.filter((id) => id !== '')));
    if (unique.length === 0) sessionStorage.removeItem(newArticlesKey(brandId));
    else sessionStorage.setItem(newArticlesKey(brandId), JSON.stringify(unique));
  } catch {
    /* storage unavailable: no badges this session */
  }
};

/** Drop one id from a brand's list (the article was opened). A no-op when it is not there. */
export const removeNewArticle = (brandId: string, id: string): void => {
  const stored = getNewArticles(brandId);
  if (!stored.includes(id)) return;
  setNewArticles(
    brandId,
    stored.filter((entry) => entry !== id),
  );
};

/** Drop every brand's list (the owner left the blog area). Other keys are left alone. */
export const clearNewArticles = (): void => {
  try {
    const keys: string[] = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key !== null && key.startsWith(NEW_ARTICLES_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* nothing to clear */
  }
};

/**
 * Whether `pathname` is inside the blog area: the list, the editor (new or by
 * id) and the preview. The root is the same literal `router.tsx` mounts the
 * four blog routes under (`BLOG_ROOT`); it is read through the path builder
 * rather than the router so a page module never imports the route table.
 */
export const isBlogPath = (pathname: string): boolean => {
  const root = ROUTE.blog();
  return pathname === root || pathname.startsWith(`${root}/`);
};
